// filepath: /home/toofoocustomer/tf-node/tf_customer_web/components/customer/ServiceAuthFrame.tsx
// file description: TOOFOO 공통 인증 UI와 trusted OAuth resume presentation 기반 relying-product identity를 함께 렌더링한다.
// authority: 공통 계정 identity는 TOOFOO로 고정하고 서비스 브랜드는 Identity resume-context의 presentation만 신뢰한다.

"use client";

import Link from "next/link";
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useState } from "react";

import {
  type AuthorizationResumeContext,
  getAuthorizationResumeContext,
} from "@/lib/customer/identity-api";

type ServiceThemeStyle = CSSProperties & {
  "--service-page-background"?: string;
  "--service-surface"?: string;
  "--service-text"?: string;
  "--service-muted-text"?: string;
  "--service-accent"?: string;
  "--service-accent-text"?: string;
  "--service-border"?: string;
  "--service-input-background"?: string;
  "--service-focus-ring"?: string;
};

type Props = {
  resume?: string;
  eyebrow: string;
  title: string;
  defaultDescription: string;
  serviceDescriptionMode?: "login" | "register" | "forgot" | "check-email" | "profile";
  standaloneThemeMode?: "LIGHT" | "DARK";
  dataE2e: string;
  children: ReactNode;
};

const STANDALONE_DARK_THEME_STYLE: ServiceThemeStyle = {
  "--service-page-background": "#08090b",
  "--service-surface": "#13151a",
  "--service-text": "#f4f5f7",
  "--service-muted-text": "#aeb4bf",
  "--service-accent": "#f4f5f7",
  "--service-accent-text": "#0b0c0f",
  "--service-border": "#30343c",
  "--service-input-background": "#ffffff",
  "--service-focus-ring": "rgba(244, 245, 247, 0.18)",
};

function themeStyle(
  context: AuthorizationResumeContext | null,
): ServiceThemeStyle | undefined {
  if (!context) return undefined;

  const presentation = context.presentation;
  return {
    "--service-page-background": presentation.page_background_color,
    "--service-surface": presentation.surface_color,
    "--service-text": presentation.text_color,
    "--service-muted-text": presentation.muted_text_color,
    "--service-accent": presentation.accent_color,
    "--service-accent-text": presentation.accent_text_color,
    "--service-border": presentation.border_color,
    "--service-input-background": presentation.input_background_color,
    "--service-focus-ring": presentation.focus_ring_color,
  };
}

export function ServiceAuthFrame({
  resume,
  eyebrow,
  title,
  defaultDescription,
  serviceDescriptionMode,
  standaloneThemeMode = "LIGHT",
  dataE2e,
  children,
}: Props) {
  const [context, setContext] = useState<AuthorizationResumeContext | null>(null);
  const [contextState, setContextState] = useState<"idle" | "loading" | "ready" | "invalid">(
    resume ? "loading" : "idle",
  );

  useEffect(() => {
    if (!resume) return;

    let cancelled = false;
    void (async () => {
      try {
        const result = await getAuthorizationResumeContext(resume);
        if (cancelled) return;
        setContext(result);
        setContextState("ready");
      } catch {
        if (cancelled) return;
        setContext(null);
        setContextState("invalid");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resume]);

  if (contextState === "invalid") {
    return (
      <main className="auth-page">
        <section className="status-card" data-e2e="customer-authorization-resume-invalid">
          <div className="status-mark is-error">!</div>
          <h1>로그인 요청이 만료되었습니다</h1>
          <p>이용하려던 서비스로 돌아가 다시 로그인해 주세요.</p>
          <div className="status-actions">
            <Link className="secondary-button link-button" href="/login">TOOFOO 로그인</Link>
            <Link className="text-link" href="/">Customer 홈</Link>
          </div>
        </section>
      </main>
    );
  }

  const isServiceFlow = Boolean(resume && context);
  const useStandaloneTheme = !resume && standaloneThemeMode === "DARK";
  const useThemeTokens = isServiceFlow || useStandaloneTheme;
  const resolvedThemeMode = context?.presentation.theme_mode
    ?? (useStandaloneTheme ? "DARK" : "LIGHT");
  const resolvedThemeStyle = themeStyle(context)
    ?? (useStandaloneTheme ? STANDALONE_DARK_THEME_STYLE : undefined);
  const serviceBrandLabel = context?.presentation.brand_label ?? "";
  let description = defaultDescription;
  if (context && serviceDescriptionMode === "login") {
    description = `${context.client_name} 이용을 위해 TOOFOO 계정으로 로그인합니다.`;
  } else if (context && serviceDescriptionMode === "register") {
    description = `${context.client_name} 이용을 위한 TOOFOO 공통 계정을 만듭니다.`;
  } else if (context && serviceDescriptionMode === "forgot") {
    description = `${context.client_name} 이용 계정의 비밀번호를 재설정합니다.`;
  } else if (context && serviceDescriptionMode === "check-email") {
    description = `${context.client_name} 이용을 계속하려면 이메일 확인을 완료해 주세요.`;
  } else if (context && serviceDescriptionMode === "profile") {
    description = `${context.client_name} 이용에 필요한 공통 고객 정보를 확인합니다.`;
  }

  return (
    <main
      className={`auth-page ${useThemeTokens ? "service-auth-theme" : ""}`}
      data-theme-mode={resolvedThemeMode}
      data-e2e={isServiceFlow ? `${dataE2e}-service` : dataE2e}
      style={resolvedThemeStyle}
    >
      <section className="auth-card">
        <Link className="brand-link" href="/">TOOFOO</Link>
        <div className="auth-heading">
          <p className="eyebrow">{isServiceFlow ? "TOOFOO ACCOUNT" : eyebrow}</p>
          <h1>{contextState === "loading" ? "서비스 연결 확인 중" : title}</h1>
          <p>{description}</p>
        </div>
        {contextState === "loading" ? (
          <div className="auth-context-loading" data-e2e="customer-authorization-context-loading">
            안전한 로그인 요청을 확인하고 있습니다.
          </div>
        ) : children}
        {isServiceFlow && context ? (
          <div className="service-brand-identity" data-e2e="customer-relying-service-identity">
            <span className="service-brand-kicker">CONNECTED SERVICE</span>
            <strong className="service-brand-label">{serviceBrandLabel}</strong>
            <small>{context.client_name}</small>
          </div>
        ) : null}
      </section>
    </main>
  );
}
